import { EntityInfoCard, useEntity } from '@backstage/plugin-catalog-react';
import { Alert, Button, ButtonLink, Flex, Text } from '@backstage/ui';

import { buildMcpJsonSnippet, buildVscodeInstallUrl } from './buildVscodeInstallUrl';
import { isApiEntityMcpServer, McpRemote } from './isApiEntityMcpServer';

import { copyToClipboard } from '../copyToClipboard';
import styles from './McpServerInstallCard.module.css';

type RemoteInstallOptionProps = {
  serverName: string;
  remote: McpRemote;
};

const RemoteInstallOption = ({ serverName, remote }: RemoteInstallOptionProps) => {
  const vscodeUrl = buildVscodeInstallUrl(serverName, remote);
  const snippet = buildMcpJsonSnippet(serverName, remote);

  return (
    <div className={styles.remoteRow}>
      <Flex direction="column" gap="1" className={styles.info}>
        <Text className={styles.transport}>{remote.type}</Text>
        <Text className={styles.url}>{remote.url}</Text>
      </Flex>
      <div className={styles.actions}>
        <ButtonLink href={vscodeUrl} variant="primary">
          Add to Visual Studio Code
        </ButtonLink>
        <Button variant="secondary" onClick={() => copyToClipboard(remote.url)}>
          Copy URL
        </Button>
        <Button variant="tertiary" onClick={() => copyToClipboard(snippet)}>
          Copy JSON
        </Button>
      </div>
    </div>
  );
};

export const McpServerInstallCard = () => {
  const { entity } = useEntity();

  if (!isApiEntityMcpServer(entity)) {
    return null;
  }

  const remotes = entity.spec.remotes ?? [];
  const serverName = entity.metadata.name;

  return (
    <EntityInfoCard title="Install" className={styles.card}>
      <Alert
        status="warning"
        icon
        title="Make sure you trust the owner of this MCP server and that you know what you are installing."
        mb="2"
      />
      {remotes.length === 0 ? (
        <Text>This MCP server has no remotes configured.</Text>
      ) : (
        remotes.map((remote, idx) => (
          <RemoteInstallOption
            key={`${remote.type}-${remote.url}-${idx}`}
            serverName={serverName}
            remote={remote}
          />
        ))
      )}
    </EntityInfoCard>
  );
};
